import * as z from 'zod';
import { ACTIVITY_DURATIONS, ACTIVITY_FREQUENCIES, ACTIVITY_INTENSITIES } from '@/lib/assessment/fitnessAge';

// An activity answer (#56): one of the codes, or '' when not asked.
const answer = <T extends string>(codes: readonly [T, ...T[]]) => z.union([z.literal(''), z.enum(codes)]).optional();

export const examSchema = z.object({
  // History Assessment
  history: z.object({
    chronicDiseases: z.array(z.string()).optional(),
    additionalOtherChronic: z.boolean().optional(),
    additionalOtherChronicDetails: z.string().optional(),
    additionalSurgery: z.boolean().optional(),
    additionalSurgeryDetails: z.string().optional(),
    additionalMedications: z.boolean().optional(),
    additionalMedicationsDetails: z.string().optional(),
    additionalAllergies: z.boolean().optional(),
    additionalAllergiesDetails: z.string().optional(),
    familyChronicDiseases: z.array(z.string()).optional(),
    otherChronicDiseasesCheck: z.boolean().optional(),
    otherChronicDiseasesDetails: z.string().optional(),
    familyHistory: z.string().optional()
  }),

  // Physical Assessment
  height: z.number().min(0).max(300),
  weight: z.number().min(0).max(500),
  bodyFatPercentage: z.number().min(0).max(100).optional(),
  muscleMass: z.number().min(0).max(200).optional(),
  bmi: z.number().min(0).max(50).optional(),
  bmr: z.number().min(0).max(5000).optional(),
  hba1c: z.number().min(0).max(20).optional(),
  triglycerides: z.number().min(0).max(1000).optional(),
  cholesterol: z.number().min(0).max(500).optional(),
  bloodPressure: z.object({
    systolic: z.number().min(0).max(250),
    diastolic: z.number().min(0).max(150)
  }),
  pulse: z.number().min(0).max(200).optional(),
  oxygenLevel: z.number().min(0).max(100).optional(),
  glucoseLevel: z.number().min(0).max(400).optional(),
  ph: z.number().min(0).max(14).optional(),
  pco2: z.number().min(0).max(200).optional(),
  po2: z.number().min(0).max(500).optional(),
  hco3: z.number().min(0).max(50).optional(),
  baseExcess: z.number().min(-30).max(30).optional(),
  sodium: z.number().min(0).max(200).optional(),
  potassium: z.number().min(0).max(10).optional(),
  chloride: z.number().min(0).max(150).optional(),
  calcium: z.number().min(0).max(20).optional(),
  urea: z.number().min(0).max(200).optional(),
  creatinine: z.number().min(0).max(10).optional(),
  troponin: z.number().min(0).max(100).optional(),
  hemoglobin: z.number().min(0).max(30).optional(),
  uricAcid: z.number().min(0).max(20).optional(),
  // Adults' waist and neck (cm) and the three activity questions behind the
  // computed fitness age (#56); optional, 0 / '' = not measured.
  waistCircumference: z.number().min(0).max(300).optional(),
  neckCircumference: z.number().min(0).max(100).optional(),
  activityFrequency: answer(ACTIVITY_FREQUENCIES),
  activityDuration: answer(ACTIVITY_DURATIONS),
  activityIntensity: answer(ACTIVITY_INTENSITIES),
  
  // Eye Assessment
  rightEye: z.number().min(0).max(20).optional(),
  leftEye: z.number().min(0).max(20).optional(),
  
  // Hearing Assessment
  rightEar: z.enum(['Normal', 'Mild', 'Moderate', 'Severe']).nullable().optional(),
  leftEar: z.enum(['Normal', 'Mild', 'Moderate', 'Severe']).nullable().optional(),
  
  // Dental Assessment
  cavities: z.number().min(0).optional(),
  missingTeeth: z.number().min(0).optional(),
  needsCleaning: z.boolean().optional(),
  needsOrthodontics: z.boolean().optional(),
  
  
  // Additional Notes
  notes: z.string().optional()
});

export type ExamFormData = z.infer<typeof examSchema>;

export const defaultExamValues: ExamFormData = {
  history: {
    chronicDiseases: [],
    additionalOtherChronic: false,
    additionalOtherChronicDetails: '',
    additionalSurgery: false,
    additionalSurgeryDetails: '',
    additionalMedications: false,
    additionalMedicationsDetails: '',
    additionalAllergies: false,
    additionalAllergiesDetails: '',
    familyChronicDiseases: [],
    otherChronicDiseasesCheck: false,
    otherChronicDiseasesDetails: '',
    familyHistory: ''
  },
  height: 0,
  weight: 0,
  bodyFatPercentage: 0,
  muscleMass: 0,
  bmi: 0,
  bmr: 0,
  hba1c: 0,
  triglycerides: 0,
  cholesterol: 0,
  bloodPressure: {
    systolic: 0,
    diastolic: 0
  },
  pulse: 0,
  oxygenLevel: 0,
  glucoseLevel: 0,
  ph: 0,
  pco2: 0,
  po2: 0,
  hco3: 0,
  baseExcess: 0,
  sodium: 0,
  potassium: 0,
  chloride: 0,
  calcium: 0,
  urea: 0,
  creatinine: 0,
  troponin: 0,
  hemoglobin: 0,
  uricAcid: 0,
  waistCircumference: 0,
  neckCircumference: 0,
  activityFrequency: '',
  activityDuration: '',
  activityIntensity: '',
  rightEye: 0,
  leftEye: 0,
  rightEar: null,
  leftEar: null,
  cavities: 0,
  missingTeeth: 0,
  needsCleaning: false,
  needsOrthodontics: false,
  notes: ''
};